/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { SDKContext } from "../../../context/SDKContext"
import { Icon } from "../../common/icons"
import * as Util from "../../../util"
import { RiChatPrivateFill } from "react-icons/ri"
import { MdLockClock } from "react-icons/md"
import { CgBlock } from "react-icons/cg"
import { ChatCloseIcon } from "./_chatCloseIcon"
import { ChatReplyIcon } from "./_chatReplyIcon"
import { ChatUserName } from "./_chatUserName"
import { ChatMessageText } from "./_chatMessageText"
import { ChatHoverButtons } from "./_chatHoverButtons"
import { MessageModel } from "../../../recoil/atoms/chats/types"
import { useReactions } from "../../../hooks/useReactions"
import {
  reactionsStatusAtom,
  reactionsStatusSelector
} from "../../../recoil/atoms/chats"
import { chatIdsSelector } from "../../../recoil/atoms/chats"
import { ChatStyles } from "./index"
import { UserAvatar } from "../../common/avatar"
import { UserType } from "../../../constants/enums"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import { useTranslation } from "react-i18next"
import ChatAttachment from "./_chatAttachment"
import useConfig from "src/hooks/useConfig"

interface ChatMessageProps {
  borderColor: string
  chatBubbleRef?: React.RefObject<HTMLDivElement>
  isModerator: boolean
  isPrivateMessage: boolean
  toggleChat?: any
  toggleShowAttachment?: (link: Partial<MessageModel>) => void
  isApproved: boolean
  handleChatMessageDelete: (id: string) => void
  handleReply: (id: string) => void
  isPendingForAction: boolean
  isMobile: boolean
  setIsReply: any
  positionModal: { x: number; y: number }
  handleAssignPosition: (e: any) => void
  isReply: boolean
  isTransparent?: boolean
  isShowReplyIcon?: boolean
  colorDisk: string
  chat: Partial<MessageModel>
  scrollDown: () => void
  setSessionStorage: any
  isAudience: boolean
  localUserName: string
  localUserId: string
  styleschat: ChatStyles
  canSendChat: boolean
  chatApprovalRequired: boolean
}

const _ChatMessage: React.FC<ChatMessageProps> = ({
  borderColor,
  chatBubbleRef,
  isModerator,
  isPrivateMessage,
  toggleChat,
  toggleShowAttachment,
  isApproved,
  handleChatMessageDelete,
  handleReply,
  isPendingForAction,
  isMobile,
  setIsReply,
  positionModal,
  handleAssignPosition,
  isReply,
  isTransparent,
  isShowReplyIcon,
  colorDisk,
  chat,
  scrollDown,
  setSessionStorage,
  isAudience,
  localUserName,
  localUserId,
  styleschat,
  canSendChat,
  chatApprovalRequired
}) => {
  const { t } = useTranslation()
  const meetingClient = React.useContext(SDKContext)
  const { isTablet } = useRecoilValue(viewDependingLayoutAtom)
  const [hovered, setHovered] = React.useState<boolean>(false)
  const setReactionsStatus = useSetRecoilState(reactionsStatusSelector)
  const setChats = useSetRecoilState(chatIdsSelector)
  const { disableReplies } = useConfig()
  const reactionsStatusList = useRecoilValue(reactionsStatusAtom)
  const reactionsRef = React.useRef<HTMLDivElement>(null)
  const localEmodji = React.useMemo(() => {
    const array = reactionsStatusList.filter(item => item[0] === chat?.id)
    return array.length > 0 ? array[0][1] : []
  }, [chat?.id, reactionsStatusList])

  const isLocalUserChatAuthor = React.useMemo(() => {
    return localUserId === chat?.author.id ? true : false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.author.id])

  const isAuthorModerator = React.useMemo(() => {
    return chat?.author.type === UserType.MODERATOR
  }, [chat?.author.type])

  const backgroundColor = React.useMemo(() => {
    if (isTransparent) {
      return "none"
    }

    return styleschat?.servicePanelElementBackground ?? "white"
  }, [isTransparent])

  const isCloseIcon = React.useMemo<boolean>(() => {
    if (isMobile) {
      return false
    }
    return (
      (isModerator && hovered) ||
      (!isModerator && hovered && isLocalUserChatAuthor)
    )
  }, [
    hovered,
    isLocalUserChatAuthor,
    isMobile,
    isModerator,
    // isReply,
    isTransparent
  ])

  const isChatReply = React.useMemo(() => {
    if (isMobile) {
      return isReply ? false : true
    }
    return true
  }, [isMobile, isReply])

  const removeReaction = (emodji: string) => {
    meetingClient?.removeReaction(chat?.id, emodji)

    if (localEmodji.length > 0) {
      const reactions = localEmodji.filter(item => {
        return item !== emodji
      })
      const data = {
        [`${chat?.id}`]: reactions
      }

      setReactionsStatus({
        type: "DELETE",
        payload: { id: chat?.id, reaction: emodji }
      })

      setSessionStorage(data, "rewrite")
    }
  }

  const handleReaction = React.useCallback(
    (emodji: string): void => {
      meetingClient?.sendReaction(chat?.id, emodji)
      if (localEmodji.length > 0) {
        const reactions = [emodji].concat(localEmodji)
        const data = {
          [`${chat?.id}`]: reactions
        }
        setSessionStorage(data, "add")
        setReactionsStatus({
          type: "UPDATE",
          payload: { id: chat?.id, reactions: reactions }
        })
      } else {
        const reactions = [emodji]
        const data = {
          [`${chat?.id}`]: reactions
        }

        setSessionStorage(data, "add")
        setReactionsStatus({ type: "ADD", payload: [chat?.id, reactions] })
      }

      if (isMobile) {
        setIsReply(false)
      }
    },
    [
      chat?.id,
      localEmodji,
      meetingClient,
      setReactionsStatus,
      setSessionStorage
    ]
  )

  /* const handleDeleteReaction = React.useCallback((emodji: string) => {

  }, []) */

  const { ReactionsTrigger, ReactionItem, ReactionsModal, isOpenReplyModal } =
    useReactions(handleReaction, Util.emodjiList, scrollDown)

  const isEmptyReactions = React.useMemo(() => {
    return (
      Object.entries(Util.removeUnnecessaryReactions(chat?.reactions))
        .length === 0
    )
  }, [chat?.reactions])

  const isReactionsTrigger = React.useMemo(() => {
    if (isMobile) {
      return !isLocalUserChatAuthor && isReply ? true : false
    }
    return !isLocalUserChatAuthor ? true : false
  }, [isLocalUserChatAuthor, isMobile, isReply])

  const heightHoverButton = React.useMemo(() => {
    const messageContainer = document.getElementById(`message-${chat?.id}`)

    if (
      chat?.reactions !== undefined &&
      !isEmptyReactions &&
      messageContainer?.offsetHeight <= 90 &&
      reactionsRef?.current?.offsetHeight < 30
    ) {
      return "60%"
    }
    if (
      chat?.reactions !== undefined &&
      !isEmptyReactions &&
      messageContainer?.offsetHeight <= 90 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "50%"
    }
    if (
      chat?.reactions !== undefined &&
      !isEmptyReactions &&
      messageContainer?.offsetHeight <= 120 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "45%"
    }
    if (
      chat?.reactions !== undefined &&
      !isEmptyReactions &&
      messageContainer?.offsetHeight >= 120 &&
      reactionsRef?.current?.offsetHeight < 30
    ) {
      return "65%"
    }
    if (
      chat?.reactions !== undefined &&
      !isEmptyReactions &&
      messageContainer?.offsetHeight >= 120 &&
      reactionsRef?.current?.offsetHeight > 30
    ) {
      return "50%"
    }
    return "100%"
  }, [chat?.reactions, chat?.id, hovered, !isEmptyReactions])

  const handleDeleteBadMessage = () => {
    setChats({
      type: "REMOVE",
      payload: { id: chat?.id }
    })
  }

  const handleMouseEnter = () => {
    if (!isMobile) {
      setHovered(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isMobile) {
      setHovered(false)
    }
  }

  const hoverStyle = React.useMemo(() => {
    return { opacity: 1 }
  }, [])

  const avatarStyles = React.useMemo(() => {
    return { width: "1.5rem", height: "1.5rem" }
  }, [])

  // show hover button when mouse is hover or chat is published
  const showHoverButton = hovered || chat?.published

  return (
    <Box
      bgColor={isTransparent ? "rgba(0, 0, 0, 0.5)" : backgroundColor}
      w="100%"
      id={`message-${chat?.id}`}
      key={`message-${chat?.id}-key`}
      sx={{
        p: isTransparent ? "0.2rem 0rem" : "0.2rem",
        paddingTop: chat.attachmentURL && isMobile ? ".33rem" : null
      }}
      borderRadius="0.6rem"
      borderWidth={isTransparent ? "0px" : "2px"}
      borderStyle={
        chat?.badMessage ? "dotted" : isApproved ? "solid" : "dotted"
      }
      borderColor={borderColor}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      _hover={hoverStyle}
      ref={chatBubbleRef}
      pos="relative"
      display="flex"
      width={isMobile ? "fit-content" : "100%"}
      minWidth={
        isMobile && !canSendChat && isReply && isShowReplyIcon
          ? "250px"
          : isMobile && (isOpenReplyModal ? "290px" : "70px")
      }
      flexDirection={"column"}
    >
      {!isMobile && (
        <ReactionsModal styleschat={styleschat} isMobile={isMobile} />
      )}
      {isCloseIcon && (
        <ChatCloseIcon
          styleschat={styleschat}
          isMobile={isMobile}
          callback={
            chat?.badMessage ? handleDeleteBadMessage : handleChatMessageDelete
          }
          id={chat?.id}
        />
      )}

      <Flex
        textColor="gray"
        pos="relative"
        marginBottom={isMobile && isOpenReplyModal && !isTransparent && "5px"}
        flexDirection="row"
        justifyContent="space-between"
        width="100%"
        onClick={
          isMobile && isShowReplyIcon && !chat?.badMessage && !isTransparent
            ? e => {
                e.stopPropagation()
                handleReply(chat?.id)
                e.stopPropagation()
              }
            : null
        }
      >
        {!isPrivateMessage && !isMobile && !chat?.badMessage && (
          <ChatHoverButtons
            chatApprovalRequired={chatApprovalRequired}
            id={chat?.id}
            isAuthorModerator={isAuthorModerator}
            toggleChat={(...args) => {
              !!chat.attachmentURL
                ? toggleShowAttachment(chat)
                : toggleChat(...args)
            }}
            hasAttachment={!!chat.attachmentURL}
            hovered={showHoverButton}
            published={chat?.published}
            isApproved={isApproved}
            isModerator={isModerator}
            positionX={
              chatApprovalRequired === true &&
              !(isLocalUserChatAuthor && isModerator)
                ? "-1%"
                : "0%"
            }
            heightHoverButton={heightHoverButton}
            alignButtons={chat?.reactions !== undefined ? "end" : "center"}
            isSmallIndex={true}
            styleschat={styleschat}
          />
        )}
        <Flex flexDirection="column">
          <Flex
            marginLeft={isTransparent ? "0.33rem" : "0.1rem"}
            marginTop="0.1rem"
          >
            <UserAvatar
              avatar={chat?.author?.avatar}
              avatarStyle={avatarStyles}
              name={chat?.author?.name}
              size="xs"
              src={chat?.author?.avatar}
              colorDisk={colorDisk}
            />
          </Flex>
          <Flex flexDirection="row">
            <Flex
              alignItems="end"
              height={"100%"}
              textColor={"gray.500"}
              zIndex={10000000}
              pl="0.35rem"
              pt={!isTransparent && "0.3rem"}
              width="1.3rem"
              minWidth="24px"
            >
              {isPrivateMessage && !isTransparent && !chat?.badMessage && (
                <Icon
                  src={RiChatPrivateFill}
                  tooltip="Private message"
                  color={
                    styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                  }
                />
              )}
              {!isMobile && chat?.badMessage && !isTransparent && (
                <Icon
                  src={CgBlock}
                  tooltip={t("Message blocked")}
                  color={
                    styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                  }
                />
              )}
              {!isMobile &&
                !isModerator &&
                isPendingForAction &&
                !isTransparent &&
                !chat?.badMessage && (
                  <Icon
                    src={MdLockClock}
                    textColor={
                      styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                    }
                    tooltip={t("Pending approval")}
                  />
                )}
              {!isMobile &&
                !isModerator &&
                !isPendingForAction &&
                !isApproved &&
                !isTransparent &&
                !chat?.badMessage && (
                  <Icon
                    src={MdLockClock}
                    textColor={
                      styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                    }
                    tooltip={t("Pending approval")}
                  />
                )}
            </Flex>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          width="100%"
          marginLeft="0.33rem"
          marginRight={isMobile && "0.33rem"}
        >
          <ChatUserName
            isMobile={isMobile}
            styleschat={styleschat}
            isTransparent={isTransparent}
            isModerator={isModerator}
            isPrivateMessage={isPrivateMessage}
            author={chat?.author}
            targetUser={chat?.targetUser}
            localUserName={localUserName}
            localUserId={localUserId}
            message={chat?.message}
            hovered={
              showHoverButton &&
              isModerator &&
              !isPrivateMessage &&
              !chat?.badMessage
            }
          />
          {chat.attachmentThumbnailURL && (
            <ChatAttachment chat={chat} hasMessage={!!chat?.message} />
          )}
          {/* {!isTransparent && ( */}
          <ChatMessageText
            styleschat={styleschat}
            isMobile={isMobile}
            hovered={
              showHoverButton &&
              isModerator &&
              !isPrivateMessage &&
              !chat?.badMessage
            }
            message={chat?.message}
            isApproved={isApproved}
            isTransparent={isTransparent}
          />
          {/* )} */}
          <Flex
            marginTop={!isTransparent && "0.1rem"}
            flexWrap="wrap"
            ref={reactionsRef}
          >
            {chat?.reactions !== undefined && (
              <>
                {Object.entries(
                  Util.removeUnnecessaryReactions(chat?.reactions)
                ).map((reaction, index) => {
                  return (
                    <ReactionItem
                      emodji={Util.convertToUnicode(
                        reaction[0],
                        Util.emodjiList
                      )}
                      uniqKey={`reaction-message-${chat?.id}-${reaction[0]}-${index}`}
                      total={reaction[1]}
                      isAddedReaction={localEmodji.includes(reaction[0])}
                      handleAddReaction={e => {
                        e.stopPropagation()
                        handleReaction(reaction[0])
                      }}
                      handleRemoveReaction={e => {
                        e.stopPropagation()
                        removeReaction(reaction[0])
                      }}
                      styleschat={styleschat}
                      isMobile={isMobile}
                    />
                  )
                })}
              </>
            )}
          </Flex>
        </Flex>

        <Flex>
          {!isTransparent && (
            <Flex
              wordBreak="break-word"
              textColor={styleschat?.commentTextColor ?? "inherit"}
              fontFamily={styleschat?.commentFontFamily ?? "inherit"}
              flexDirection="column"
            >
              <Flex
                whiteSpace="nowrap"
                marginLeft={isMobile ? "auto" : "0.25rem"}
              >
                <Text
                  fontSize="0.75rem"
                  color={styleschat?.servicePanelElementLabels ?? "#718096"}
                >
                  {Util.formatTime24h(chat?.timestamp)}
                </Text>
              </Flex>
              <Flex marginTop="auto" marginBottom="0.1rem">
                {isMobile &&
                  !isModerator &&
                  !isPendingForAction &&
                  !isApproved &&
                  !isTransparent &&
                  !chat?.badMessage && (
                    <Icon
                      src={MdLockClock}
                      textColor={
                        styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                      }
                      tooltip={t("Pending approval")}
                      containerStyles={{ marginLeft: "auto" }}
                      marginRight={
                        isMobile &&
                        isReply &&
                        isShowReplyIcon &&
                        !isTransparent &&
                        "0.2rem"
                      }
                    />
                  )}
                {isMobile &&
                  !isModerator &&
                  isPendingForAction &&
                  !isTransparent &&
                  !chat?.badMessage && (
                    <Icon
                      src={MdLockClock}
                      textColor={
                        styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                      }
                      tooltip={t("Pending approval")}
                      containerStyles={{ marginLeft: "auto" }}
                      marginRight={"0.2rem"}
                    />
                  )}
                {isReactionsTrigger && canSendChat && (
                  <Flex
                    marginLeft="auto"
                    alignItems="end"
                    fontSize={isMobile && "1.15em"}
                    height={"100%"}
                    marginRight={
                      (isModerator ||
                        (!isModerator && isLocalUserChatAuthor)) &&
                      isMobile &&
                      isReply &&
                      isShowReplyIcon &&
                      !isTransparent &&
                      "0.35rem"
                    }
                    zIndex={!isMobile && 10000000}
                  >
                    <ReactionsTrigger styleschat={styleschat} />
                  </Flex>
                )}
                {disableReplies === false &&
                  isChatReply &&
                  !chat?.badMessage && (
                    <ChatReplyIcon
                      styleschat={styleschat}
                      id={chat?.id}
                      isMobile={isMobile}
                      isShowReplyIcon={isShowReplyIcon}
                      handleReply={handleReply}
                    />
                  )}
                {(isModerator || (!isModerator && isLocalUserChatAuthor)) &&
                  isMobile &&
                  isReply &&
                  isShowReplyIcon &&
                  !isTransparent && (
                    <ChatCloseIcon
                      styleschat={styleschat}
                      isMobile={isMobile}
                      callback={
                        chat?.badMessage
                          ? handleDeleteBadMessage
                          : handleChatMessageDelete
                      }
                      id={chat?.id}
                    />
                  )}
                {isMobile && chat?.badMessage && !isTransparent && (
                  <Icon
                    id="oidar"
                    src={CgBlock}
                    tooltip={t("Message blocked")}
                    containerStyles={{ marginLeft: "auto" }}
                    color={
                      styleschat?.servicePanelElementPrimaryText ?? "#4A5568"
                    }
                  />
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      {isMobile && isReply && isShowReplyIcon && !isTransparent && (
        <>
          <ReactionsModal
            styleschat={styleschat}
            isMobile={isMobile}
            customStyles={{
              position: "relative",
              bottom: "unset",
              left: "unset",
              marginBottom: "0.2rem"
            }}
          />
        </>
      )}
    </Box>
  )
}

export const ChatMessage = React.memo(_ChatMessage)
